import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import throttle from 'lodash/throttle';
import { activateScripts } from '@zalando-internal/zalando-consent-api';
import { initObservability } from '@pegasus/observability/client';
import {
    WebVitalsCollector,
    CMAGCollector,
    FEATURE_KEY,
    otlpCollector,
    generateAttributesFromWindow,
} from '@pegasus/web-vitals';
import { initSentry } from '@pegasus/sentry';
import { Translations } from '@pegasus/translation';
import { loadConfig, UrlSource } from '@browsing/config';
import 'what-input';
import 'intersection-observer';
import 'whatwg-fetch';

import { getTranslations } from '../locales/translations';
import { setClientDimensions, setClientOffset } from './helpers/clientDimensions';
import { getVisibilityChangeEventName, setTabVisibility } from './helpers/tabVisibility';
import { isIE } from './helpers/browser';
import { addClassListToSVG } from './helpers/shims';

// just require it so it gets copied on deploy by webpack
require('../favicon.ico');

import configureStore from './redux/configureStore';
import AppRoot from './containers/AppRoot';

const config = loadConfig();

if (config.isEnabled('sentry') && config.sentry) {
    initSentry();
}

initObservability({
    application: 'pegasus-mylounge',
    commonAttributes: { ...generateAttributesFromWindow(), app_domain: config.appdomain },
});
const locale = config.localeCode;

/* eslint-disable no-console */
// add third-party consents scripts based on consent
activateScripts().then(
    () => console.log('Third party scripts with consent are activated'),
    () => console.error('An error has occurred activating scripts'),
);

WebVitalsCollector([
    // @ts-ignore
    CMAGCollector({ featureKey: FEATURE_KEY.mylounge, flowId: window?.flowId }),
    otlpCollector(),
]);

let initialState: any & { date?: string; processingTime?: number } = {};

// Try to re-use initial state from server side rendering on client
// as window is undefined on server, we need to wrap this into try/catch
try {
    // @ts-ignore
    if (window.__INITIAL_STATE__) {
        // @ts-ignore
        initialState = { ...initialState, ...window.__INITIAL_STATE__ };
        delete initialState.date;
        delete initialState.processingTime;
        // @ts-ignore
        delete window.__INITIAL_STATE__;
    }
} catch (e) {
    console.error('catch', e);
}

// SVG classList shim for IE
if (isIE()) {
    addClassListToSVG();
}

// This is to make webpack async load from the correct place
if (!__DEV__) {
    // eslint-disable-next-line
    __webpack_public_path__ = config.getUrl(UrlSource.ASSETS);
}

// Create redux store.
const store = configureStore(initialState);

// Now that we have the Redux store, we can create our routes. We provide
// the store to the route definitions so that routes have access to it for
// hooks such as `onEnter`.

let root: Root | null = null;
let translations: Translations | null = null;

const renderApp = () => {
    if (root === null || translations === null) {
        throw new Error(
            'Application has not been started properly. Initialize `root` and `translations` before rendering the app.',
        );
    }
    if (__DEV__) {
        const RedBox = require('redbox-react');
        try {
            root.render(<AppRoot store={store} translations={translations} config={config} />);
        } catch (e) {
            console.error(e);
            root.render(<RedBox error={e} />);
        }
    } else {
        root.render(<AppRoot store={store} translations={translations} config={config} />);
    }
};

const startApp = async () => {
    // Set the client device
    setClientDimensions(store);
    setClientOffset(store);
    setTabVisibility(store);

    window.addEventListener(
        'resize',
        throttle(() => setClientDimensions(store), 200),
    );
    window.addEventListener(
        'scroll',
        throttle(() => setClientOffset(store), 400),
    );
    document.addEventListener(getVisibilityChangeEventName(), () => setTabVisibility(store));

    translations = await getTranslations(locale);
    // client side rendering will mount the app into #react-container
    // runtime errors in dev will show redbox overlay with stack trace
    const container = document.getElementById('react-container');
    root = createRoot(container);
    renderApp();
};

startApp().then(() => {
    // Re-render the app instead of refreshing the whole page when a code change occurs below AppRoot.
    if (__DEV__) {
        if (module.hot) {
            module.hot.accept('./containers/AppRoot', () => {
                renderApp();
            });
        }
    }
});
